import { channel, delay } from 'redux-saga';
import { call, cancel, fork, put, race, select, take } from 'redux-saga/effects';
import { clearMessage, setMessage, WARNING } from '../../containers/FormMessage/actions';
import * as notify from '../../containers/layout/Notifications/actions';
import { showCongratulations, updateBetCashOutStatus } from '../../containers/openBets/actions';
import { getOpenBet } from '../../containers/openBets/selectors';
import { serviceUnavailable } from '../app/actions';
import { ConnectionError, ServiceUnavailableError, UnauthorizedAccessError } from '../app/errors';
import { getBet } from '../bets/rest';
import { fetchCashInPrice } from '../cashInPrices/saga';
import { close, open } from '../modal/actions';
import { isAuthenticated } from '../user/selectors';
import { ATTEMPT, CONFIRM, freezeCashInPrice, REJECT } from './actions';
import OutdatedPriceError from './errors';
import { postCashIn } from './rest';
import { accountBalanceRequest } from '../account/actions';

/* eslint-disable no-constant-condition */
export function* getCashOutStatus(betId, initialBetDelay, chan) {
  const { slipId } = yield select(getOpenBet, { betId });
  const userAuthenticated = yield select(isAuthenticated);
  const CASH_OUT_STATUS_CHECK_INTERVAL = 1000;
  const parsedBetDelay = initialBetDelay ? parseInt(initialBetDelay, 10) * 1000 : 0;
  yield call(delay, parsedBetDelay);

  while (true) {
    const requestStartTime = Date.now();
    const { betStatus } = yield call(getBet, betId, userAuthenticated, slipId);
    const getBetTimeElapsed = Date.now() - requestStartTime;
    yield put(updateBetCashOutStatus(betId, betStatus));
    yield put(chan, { betStatus });
    if (getBetTimeElapsed < CASH_OUT_STATUS_CHECK_INTERVAL) {
      yield call(delay, CASH_OUT_STATUS_CHECK_INTERVAL);
    }
  }
}

export function* watchBetCashedOut(betId, betDelay, chan) {
  const MAX_CHECK_COUNT = 10;
  let statusCheckCounter = 0;
  const getCashOutStatusChannel = yield call(channel);
  const cashOutStatusUpdateTask = yield fork(
    getCashOutStatus,
    betId,
    betDelay,
    getCashOutStatusChannel
  );

  while (true) {
    const { betStatus } = yield take(getCashOutStatusChannel);
    statusCheckCounter += 1;

    if (betStatus === 'Cashed out') {
      yield put(chan, true);
      yield cancel(cashOutStatusUpdateTask);
    } else if (statusCheckCounter >= MAX_CHECK_COUNT) {
      yield put(chan, false);
    }
  }
}

export function* cashIn(price, betId, slipId) {
  try {
    let watchBetCashedOutTask;
    yield put(freezeCashInPrice(price));
    yield put(open({ componentName: 'CashInConfirm' }));

    const { confirm } = yield race({
      confirm: take(CONFIRM),
      reject: take(REJECT)
    });

    yield put(close());

    if (confirm) {
      yield put(clearMessage());
      yield put(open({ componentName: 'CashInPending' }));

      const { betDelay } = yield call(postCashIn, {
        betCashInPrice: price,
        betId,
        slipId
      });
      const watchBetCashOutChannel = yield call(channel);
      watchBetCashedOutTask = yield fork(
        watchBetCashedOut,
        betId,
        betDelay,
        watchBetCashOutChannel
      );
      const cashedOut = yield take(watchBetCashOutChannel);
      yield cancel(watchBetCashedOutTask);
      yield put(close());
      if (cashedOut) {
        yield put(accountBalanceRequest());
        yield put(showCongratulations(betId, price));
      }
    }
  } catch (e) {
    yield put(close());
    if (e instanceof UnauthorizedAccessError) {
      yield put(open({ componentName: 'CashInFailure' }));
      yield put(notify.add('Tried to cash in bet from wrong slip.', notify.ERROR));
    } else if (e instanceof ConnectionError) {
      yield put(notify.add(
        'Connection error during cash in attempt. Please try again', notify.ERROR
      ));
    } else if (e instanceof ServiceUnavailableError) {
      yield put(serviceUnavailable());
    } else if (e instanceof OutdatedPriceError) {
      yield put(setMessage(WARNING, 'CashInFailed', null, false, 'default', 10000));
      yield fork(fetchCashInPrice, betId);
    } else {
      throw e;
    }
  }
}

export function* watchCashIn() {
  while (true) {
    const { price, betId, slipId } = yield take(ATTEMPT);
    yield call(cashIn, price, betId, slipId);
  }
}
/* eslint-enable no-constant-condition */
